import React, { useState, useRef } from "react";
import { Trophy, Flame, Skull, Zap, Coins, Play, Home, Share2, Tv, Star, Boxes, Sparkles, Recycle } from "lucide-react";
import { api, apiErr } from "../api/client";
import { usePlayer } from "../context/PlayerContext";
import { Sound } from "../game/audio";
import { AdManager } from "../game/ads";
import { toast } from "sonner";

// Rewarded/interstitial ads run through AdManager (real network on live domain, placeholder in preview).
export default function Results({ results, onPlayAgain, onMenu }) {
  const { profile, applyUser } = usePlayer();
  const [phase, setPhase] = useState("choice"); // choice | ad | submitting | done
  const [awarded, setAwarded] = useState(null);
  const [err, setErr] = useState(null);
  const submittedRef = useRef(false); // guard: never bank the same match twice (double-tap / rapid clicks)
  const adsRemoved = profile?.adsRemoved;

  const submit = async (dbl) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("submitting");
    try {
      const { data } = await api.submitMatch({ ...results, doubleReward: dbl });
      applyUser(data.user);
      setAwarded(data.awarded);
      if (data.awarded.rank === 1) Sound.victory();
      (data.awarded.achievements || []).forEach((a) => toast.success(`Award unlocked — ${a.name}! +${(a.reward?.coins || 0).toLocaleString()} coins`));
      setPhase("done");
    } catch (e) {
      setErr(apiErr(e, "Could not save match. Progress may not be recorded."));
      setAwarded({ ...results, coins: 0, xp: results.xpEarned });
      setPhase("done");
    }
  };

  const watchAd = () => {
    AdManager.playRewarded().then(() => submit(true));
  };

  const playAgain = () => {
    if (adsRemoved) { onPlayAgain(); return; }
    AdManager.playInterstitial().then(() => onPlayAgain());
  };

  const topUp = async () => {
    try { const { data } = await api.checkout("coins_value"); window.location.href = data.checkout_url; }
    catch (e) { setErr(apiErr(e, "Could not start checkout.")); }
  };

  const rank = results.rank;
  const victory = rank === 1;
  const shareText = `I ${victory ? "GOT #1" : `PLACED #${rank}`} WITH ${results.score.toLocaleString()} SMASH SCORE IN SMASHUP.IO!`;
  const share = async () => {
    try { if (navigator.share) await navigator.share({ title: "SmashUp.io", text: shareText }); else { await navigator.clipboard.writeText(shareText); alert("Result copied to clipboard!"); } } catch (e) { console.warn("Share failed", e); }
  };

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="card p-3 flex flex-col items-center gap-1">
      <Icon size={18} className={color} />
      <div className="text-lg sm:text-2xl font-black text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{label}</div>
    </div>
  );

  return (
    <div className="arena-bg grain fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto scroll-thin" data-testid="results-screen">
      <div className="card w-full max-w-md p-5 sm:p-6 pop-in my-auto">
        {/* share card header */}
        <div className={`rounded-xl p-4 text-center mb-4 ${victory ? "bg-gradient-to-br from-amber-500/20 to-orange-600/10 border border-amber-500/40" : "bg-slate-800/50 border border-slate-700"}`} data-testid="results-sharecard">
          <div className="flex items-center justify-center gap-2">
            {victory && <Star size={22} className="text-amber-400" fill="currentColor" />}
            <span className={`font-head font-black text-4xl sm:text-5xl ${victory ? "text-amber-400" : "text-white"}`}>
              {victory ? "VICTORY!" : `#${rank}`}
            </span>
            {victory && <Star size={22} className="text-amber-400" fill="currentColor" />}
          </div>
          <div className="text-slate-300 text-sm mt-1">{victory ? "You dominated the arena!" : `You finished #${rank} of ${40}`}</div>
          <div className="mt-3 font-mono text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-500" data-testid="results-score">
            {results.score.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Smash Score</div>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat icon={Flame} label="Smashed" value={results.objectsSmashed} color="text-orange-400" />
          <Stat icon={Skull} label="KOs" value={results.kills} color="text-red-400" />
          <Stat icon={Zap} label="Max Combo" value={`x${results.maxCombo}`} color="text-cyan-400" />
        </div>

        {/* rewards */}
        <div className="card p-3 mb-4 border-amber-500/30">
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2">Rewards {phase === "done" && awarded ? "Earned" : "Preview"}</div>
          <div className="flex items-center justify-around">
            <div className="flex items-center gap-2">
              <Coins size={20} className="text-amber-400" />
              <span className="font-black text-xl text-white" data-testid="results-coins">+{(phase === "done" && awarded ? awarded.coins : results.coinsEarned).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap size={20} className="text-cyan-400" />
              <span className="font-black text-xl text-white" data-testid="results-xp">+{(phase === "done" && awarded ? awarded.xp : results.xpEarned).toLocaleString()} XP</span>
            </div>
          </div>
          {phase === "done" && awarded?.personalBest && (
            <div className="mt-2 text-center text-amber-400 font-black text-sm flex items-center justify-center gap-1"><Star size={14} fill="currentColor" /> NEW PERSONAL BEST!</div>
          )}
        </div>

        {/* salvage drop */}
        {phase === "done" && awarded && (awarded.scrap || awarded.cores || awarded.vault || awarded.bpXp) ? (
          <div className="card p-3 mb-4 border-cyan-500/30 slam-in" data-testid="results-salvage">
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-2">Salvage Drop</div>
            <div className="flex items-center justify-around flex-wrap gap-y-2">
              {awarded.vault && (
                <div className="flex flex-col items-center gap-0.5" data-testid="results-vault">
                  <Boxes size={18} className="text-sky-300" />
                  <span className="font-black text-white text-sm capitalize">{awarded.vault}</span>
                  <span className="text-[9px] uppercase text-slate-400 font-bold">Vault</span>
                </div>
              )}
              {awarded.scrap ? (
                <div className="flex flex-col items-center gap-0.5" data-testid="results-scrap">
                  <Recycle size={18} className="text-emerald-400" />
                  <span className="font-black text-white text-sm">+{awarded.scrap}</span>
                  <span className="text-[9px] uppercase text-slate-400 font-bold">Scrap</span>
                </div>
              ) : null}
              {awarded.cores ? (
                <div className="flex flex-col items-center gap-0.5" data-testid="results-cores">
                  <Sparkles size={18} className="text-violet-400" />
                  <span className="font-black text-white text-sm">+{awarded.cores}</span>
                  <span className="text-[9px] uppercase text-slate-400 font-bold">Cores</span>
                </div>
              ) : null}
              {awarded.bpXp ? (
                <div className="flex flex-col items-center gap-0.5" data-testid="results-bpxp">
                  <Star size={18} className="text-amber-400" />
                  <span className="font-black text-white text-sm">+{awarded.bpXp}</span>
                  <span className="text-[9px] uppercase text-slate-400 font-bold">Pass XP</span>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {err && <div className="text-red-400 text-xs text-center mb-3" data-testid="results-error">{err}</div>}

        {/* actions */}
        {phase === "choice" && (
          <div className="flex flex-col gap-2">
            {!adsRemoved && (
              <button onClick={watchAd} className="btn-ghost py-3 flex items-center justify-center gap-2 border-amber-500/40 text-amber-300" data-testid="watch-ad-btn">
                <Tv size={18} /> WATCH AD — DOUBLE REWARDS (2x)
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => submit(false).then(() => {})} className="btn-ghost py-3 flex items-center justify-center gap-2" data-testid="claim-continue-btn">
                Continue
              </button>
              <button onClick={() => submit(false)} className="btn-primary py-3 flex items-center justify-center gap-2" data-testid="claim-play-btn">
                <Play size={18} fill="currentColor" /> Claim
              </button>
            </div>
            <div className="text-[10px] text-slate-500 text-center mt-1">Rewards are validated & saved on the server</div>
          </div>
        )}

        {phase === "submitting" && (
          <div className="text-center py-4 text-slate-400" data-testid="results-saving">Saving progress...</div>
        )}

        {phase === "done" && (
          <div className="flex flex-col gap-2">
            <button onClick={playAgain} className="btn-primary text-xl py-4 flex items-center justify-center gap-2 glow-pulse" data-testid="play-again-btn">
              <Play size={22} fill="currentColor" /> PLAY AGAIN
            </button>
            {awarded && (awarded.rank <= 3 || awarded.personalBest) && (
              <button onClick={topUp} className="btn-ghost py-2.5 flex items-center justify-center gap-2 border-amber-500/40 text-amber-300" data-testid="topup-coins-btn">
                <Coins size={16} /> Great run! Top up Coins & keep the streak
              </button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={share} className="btn-ghost py-3 flex items-center justify-center gap-2" data-testid="share-btn"><Share2 size={16} /> Share</button>
              <button onClick={onMenu} className="btn-ghost py-3 flex items-center justify-center gap-2" data-testid="results-menu-btn"><Home size={16} /> Menu</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
